import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ModalCommentsEditor } from './AssessmentTab';
import ZoomableImage from './ZoomableImage';
import { findQuestionSpan, findTranscriptSpan, rowDeduction, transcriptPageIndex, revealTextareaSpan, MarkedTextarea } from '../utils/questionFocus';
import { restorePaperNotes, stripQuestionPaperText } from '../utils/questionPaper';
import { overMaxMarkRanges } from '../utils/marksValidation';
import {
  correctTable,
  extractRubricQuestionIds,
  extractRubricQuestionMarks,
  paperTotalFromRubric,
  gradeFromTable,
  keepLastTableBlock,
  normalizeQuestionId,
  splitMarkdownRowCells,
  splitRawMarkdownCells
} from '../utils/tableToOneLine';
import '../styles/AssessmentTab.css';

function nextImageFilename(name) {
  const m = String(name).match(/^(.*?)(\d+)(\.[^.]+)?$/);
  if (!m) return null;
  const nextNum = String(parseInt(m[2], 10) + 1).padStart(m[2].length, '0');
  return `${m[1]}${nextNum}${m[3] || ''}`;
}

function renderHighlights(comments) {
  const text = String(comments || '');
  if (!text) return 'No comments available';
  const overRanges = overMaxMarkRanges(text);
  if (!overRanges.length) return text;
  const out = [];
  let cursor = 0;
  overRanges.forEach((r, i) => {
    if (r.start > cursor) out.push(text.slice(cursor, r.start));
    out.push(
      <span
        key={i}
        title="Marks exceed maximum"
        style={{
          background: '#fee2e2',
          color: '#b91c1c',
          padding: '1px 6px',
          borderRadius: 4,
          fontWeight: 700,
          border: '1px solid #fca5a5',
          display: 'inline-block'
        }}
      >
        {text.slice(r.start, r.end)}
      </span>
    );
    cursor = r.end;
  });
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}

function renderMarkdownTable(md, opts = {}) {
  const text = String(md || '').trim();
  if (!text || !text.includes('|')) return null;
  const lines = text.split('\n').map((l) => l.trim()).filter((l) => l.startsWith('|'));
  if (lines.length < 2) return null;
  const isSep = (line) => /^\|\s*:?-+:?\s*(\|\s*:?-+:?\s*)+\|?\s*$/.test(line);
  let header = null;
  const bodyRows = [];
  for (const line of lines) {
    if (isSep(line)) continue;
    if (!header) { header = splitRawMarkdownCells(line); continue; }
    bodyRows.push(splitMarkdownRowCells(line, header.length));
  }
  if (!header) return null;
  const stripBold = (s) => String(s || '').replace(/\*\*/g, '').trim();
  let seenGrandRow = false;
  const belowTotal = bodyRows.map((cells) => {
    const label = stripBold(cells[0] || '');
    if (/\bgrand\s*total\b/i.test(label)) {
      seenGrandRow = true;
      return false;
    }
    return seenGrandRow;
  });
  const isBoldRow = (cells) => cells.some((c) => /^\*\*.+\*\*$/.test(c));
  const HEADER_LABELS = {
    question: 'Qs',
    marks: 'T.M',
    deduction: 'Mistakes',
    'deduction marks': '-ve',
    obtained: 'Obt.M',
    'obtained marks': 'Obt.M'
  };
  return (
    <table style={{
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: 13,
      fontFamily: 'inherit',
      border: '1px solid var(--border-color, #e2e8f0)'
    }}>
      <thead>
        <tr style={{ background: 'var(--surface-alt, #f1f5f9)' }}>
          {header.map((h, i) => {
            const plain = stripBold(h);
            const key = plain.toLowerCase().replace(/\s+/g, ' ').trim();
            return (
              <th key={i} style={{
                padding: '8px 10px',
                textAlign: 'left',
                fontWeight: 700,
                color: 'var(--text-primary, #0f172a)',
                borderBottom: '2px solid var(--border-color, #cbd5e1)'
              }}>{HEADER_LABELS[key] || plain}</th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {bodyRows.map((cells, ri) => {
          const bold = isBoldRow(cells);
          const rowLabel = stripBold(cells[0]);
          const rowQuestionId = /total/i.test(rowLabel) ? null : normalizeQuestionId(rowLabel);
          const linked = !!(rowQuestionId && opts.linkedId && rowQuestionId === opts.linkedId);
          return (
            <tr
              key={ri}
              title={belowTotal[ri] ? 'This written label is not a rubric question, so this row is not in the total.' : undefined}
              style={{
              background: linked
                ? 'rgba(245, 158, 11, 0.28)'
                : belowTotal[ri]
                  ? 'rgba(220, 38, 38, 0.08)'
                  : bold ? 'rgba(139, 92, 246, 0.08)' : 'transparent',
              fontWeight: bold ? 700 : 400,
              outline: linked ? '2px solid #d97706' : 'none'
            }}>
              {cells.map((c, ci) => (
                <td key={ci} style={{
                  padding: '6px 10px',
                  borderBottom: '1px solid var(--border-color, #e2e8f0)',
                  color: 'var(--text-secondary, #334155)',
                  verticalAlign: 'top',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {ci === 0 && rowQuestionId && opts.onPick ? (
                    <button
                      type="button"
                      onClick={() => opts.onPick(rowQuestionId, rowDeduction(header, cells))}
                      title="Highlight this question in the transcript and the rubric"
                      style={{
                        background: linked ? '#fde68a' : 'transparent',
                        border: linked ? '1px solid #d97706' : '1px solid transparent',
                        borderRadius: 6,
                        padding: '1px 6px',
                        font: 'inherit',
                        fontWeight: 700,
                        color: 'inherit',
                        cursor: 'pointer'
                      }}
                    >
                      {stripBold(c)}
                    </button>
                  ) : stripBold(c)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default function StudentResultView({ result, rubricText, folderId, canSave, onClose, onUpdated, onRubricSaved }) {
  const [viewing, setViewing] = useState(result);
  const [images, setImages] = useState([]);
  const [imageIndex, setImageIndex] = useState(0);
  const [imageNote, setImageNote] = useState('Loading scan…');
  const [scanHidden, setScanHidden] = useState(false);
  const [showRubric, setShowRubric] = useState(() => !!(rubricText && String(rubricText).trim()));
  const [ocrDraft, setOcrDraft] = useState(result.ocr_text || '');
  const [ocrSaving, setOcrSaving] = useState(false);
  const [ocrNote, setOcrNote] = useState('');
  const [rubricDraft, setRubricDraft] = useState(() => stripQuestionPaperText(rubricText || ''));
  const [rubricSaving, setRubricSaving] = useState(false);
  const [rubricNote, setRubricNote] = useState('');
  const [linkedQuestion, setLinkedQuestion] = useState(null);
  const rubricBaseRef = useRef(stripQuestionPaperText(rubricText || ''));
  const ocrRef = useRef(null);
  const rubricRef = useRef(null);
  const apiBaseUrl = window.API_BASE_URL;

  const allowedQuestionIds = useMemo(
    () => extractRubricQuestionIds(rubricText || ''),
    [rubricText]
  );
  const rubricQuestionMarks = useMemo(
    () => extractRubricQuestionMarks(rubricText || ''),
    [rubricText]
  );
  const paperTotal = useMemo(
    () => paperTotalFromRubric(rubricText || ''),
    [rubricText]
  );

  useEffect(() => {
    let cancelled = false;
    const listed = Array.isArray(viewing.image_names) ? viewing.image_names.filter(Boolean) : [];
    const raw = viewing.image_name || viewing.image_no || '';
    const names = [];
    const seen = new Set();
    const add = (n) => {
      if (n && !seen.has(n)) {
        seen.add(n);
        names.push(n);
      }
    };
    if (listed.length) {
      listed.forEach(add);
    } else {
      String(raw).split(',').forEach((part) => {
        const front = part.trim();
        if (!front) return;
        add(front);
        const back = nextImageFilename(front);
        if (back && back !== front) add(back);
      });
    }
    if (!names.length || !folderId) {
      setImages([]);
      setImageNote('No scanned page is saved for this student.');
      return undefined;
    }
    const token = localStorage.getItem('authToken');
    Promise.all(names.map(async (name) => {
      try {
        const response = await fetch(
          `${apiBaseUrl}/scans/${folderId}/preview/${encodeURIComponent(name)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) return null;
        const data = await response.json();
        if (data && data.success && data.downloadUrl) return { url: data.downloadUrl, name };
      } catch (_) { /* missing back side */ }
      return null;
    })).then((found) => {
      if (cancelled) return;
      const ready = found.filter(Boolean);
      setImages(ready);
      setImageIndex(0);
      setImageNote(ready.length ? '' : 'The scanned page could not be loaded.');
    });
    return () => { cancelled = true; };
  }, [viewing.image_names, viewing.image_name, viewing.image_no, folderId, apiBaseUrl]);

  useEffect(() => {
    const text = stripQuestionPaperText(rubricText || '');
    setRubricDraft((current) => {
      const unchanged = current === rubricBaseRef.current;
      rubricBaseRef.current = text;
      return unchanged ? text : current;
    });
    if (text.trim()) setShowRubric(true);
  }, [rubricText]);

  const hasRubric = !!(rubricText && String(rubricText).trim());
  const rubricOpen = showRubric && hasRubric;
  const imgOpen = images.length > 0 && !scanHidden;
  const savedRubric = stripQuestionPaperText(rubricText || '');
  const ocrStatus = String(viewing.ocr_status || '').toLowerCase();
  const ocrMeta = ocrStatus === 'ok'
    ? { bg: 'linear-gradient(135deg, #10B981, #059669)', label: 'OCR OK' }
    : ocrStatus === 'unclear'
      ? { bg: 'linear-gradient(135deg, #F59E0B, #D97706)', label: 'OCR unclear' }
      : ocrStatus === 'failed'
        ? { bg: 'linear-gradient(135deg, #EF4444, #B91C1C)', label: 'OCR failed' }
        : { bg: 'linear-gradient(135deg, #94A3B8, #64748B)', label: 'no transcript' };
  const ratioPct = typeof viewing.ocr_unclear_ratio === 'number'
    ? `${Math.round(viewing.ocr_unclear_ratio * 100)}% unclear`
    : null;

  const saveOcr = async () => {
    if (ocrSaving || ocrDraft === (viewing.ocr_text || '') || !viewing.student_name) return;
    setOcrSaving(true);
    setOcrNote('');
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${apiBaseUrl}/assess/snapshots/ocr`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          folder_path: `rubrics/${folderId}`,
          student_name: viewing.student_name,
          ocr_text: ocrDraft
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Save failed (${res.status})`);
      setOcrNote('Saved');
      publish({ ...viewing, ocr_text: ocrDraft, ocr_status: 'ok', ocr_unclear_ratio: null });
    } catch (err) {
      setOcrNote(err.message || 'Save failed');
    } finally {
      setOcrSaving(false);
    }
  };

  const saveRubric = async () => {
    if (rubricSaving || rubricDraft === savedRubric) return;
    setRubricSaving(true);
    setRubricNote('');
    try {
      const token = localStorage.getItem('authToken');
      const full = restorePaperNotes(rubricDraft, rubricText || '');
      const res = await fetch(`${apiBaseUrl}/rubrics/${folderId}/content`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ content: full })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || `Save failed (${res.status})`);
      rubricBaseRef.current = rubricDraft;
      setRubricNote('Saved');
      if (onRubricSaved) onRubricSaved(full);
    } catch (err) {
      setRubricNote(err.message || 'Save failed');
    } finally {
      setRubricSaving(false);
    }
  };

  const layout = imgOpen && rubricOpen
    ? { pad: '72vw', width: 'calc(28vw - 24px)', imageLeft: '28vw', imageRight: '48vw', imageWidth: 'calc(24vw - 10px)', closeRight: 'calc(48vw + 16px)', ocrRight: '24vw', ocrWidth: '24vw' }
    : imgOpen
      ? { pad: '54vw', width: 'calc(46vw - 24px)', imageLeft: '46vw', imageRight: '24vw', imageWidth: 'calc(30vw - 20px)', closeRight: 'calc(24vw + 16px)', ocrRight: '0', ocrWidth: '24vw' }
      : rubricOpen
        ? { pad: '54vw', width: 'calc(46vw - 24px)', ocrRight: '24vw', ocrWidth: '30vw' }
        : { pad: '30vw', width: 'calc(70vw - 24px)', ocrRight: '0', ocrWidth: '30vw' };
  const current = imgOpen ? images[Math.min(imageIndex, images.length - 1)] : null;
  const stored = parseFloat(viewing.marks);
  const grand = viewing.table_comments
    ? gradeFromTable(viewing.table_comments, { allowedIds: allowedQuestionIds, rubricMarks: rubricQuestionMarks, paperTotal })
    : null;
  const marksLabel = grand && !isNaN(grand.obtained) && (isNaN(stored) || Math.abs(stored - grand.obtained) > 0.05)
    ? String(Math.round(grand.obtained * 100) / 100)
    : viewing.marks;

  const publish = (next) => {
    setViewing(next);
    if (onUpdated) onUpdated(next);
  };

  const tablePreview = viewing.table_comments
    ? keepLastTableBlock((correctTable(viewing.table_comments, { allowedIds: allowedQuestionIds, rubricMarks: rubricQuestionMarks, paperTotal }) || {}).md || viewing.table_comments)
    : '';

  const focusQuestion = (raw, second, third) => {
    const id = normalizeQuestionId(raw);
    if (!id) return;
    const deduction = third === undefined ? second : third;
    const ocrEl = ocrRef.current;
    const rubricEl = rubricRef.current;
    const ocrText = ocrEl ? ocrEl.value : ocrDraft;
    const rubricTextNow = rubricEl ? rubricEl.value : rubricDraft;
    const ocrSpan = findTranscriptSpan(ocrText, id, deduction, rubricTextNow || rubricText);
    const rubricSpan = findQuestionSpan(rubricTextNow, id);
    setLinkedQuestion({ id, ocr: ocrSpan, rubric: rubricSpan, tick: Date.now() });
    if (ocrEl && ocrSpan) revealTextareaSpan(ocrEl, ocrSpan.start, ocrSpan.end);
    if (rubricSpan && hasRubric) setShowRubric(true);
    if (ocrSpan && images.length) {
      const idx = transcriptPageIndex(ocrText, ocrSpan, images);
      if (idx >= 0) setImageIndex(idx);
    }
  };

  const paintTable = (md) => renderMarkdownTable(md, {
    linkedId: linkedQuestion ? linkedQuestion.id : null,
    onPick: focusQuestion
  });

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 10080 }}>
      {current && (
        <div
          style={{
            position: 'fixed',
            top: 'var(--header-height, 52px)',
            left: layout.imageLeft,
            right: layout.imageRight,
            bottom: 0,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10081,
            padding: 16
          }}
        >
          <div style={{ position: 'relative' }}>
            <button
              type="button"
              onClick={() => setScanHidden(true)}
              title="Hide scan"
              style={{
                position: 'fixed',
                top: 'calc(var(--header-height, 52px) + 16px)',
                right: layout.closeRight,
                background: '#EF4444',
                color: 'white',
                border: '2px solid white',
                borderRadius: '50%',
                width: 38,
                height: 38,
                cursor: 'pointer',
                fontWeight: 700,
                fontSize: 20,
                lineHeight: '34px',
                zIndex: 10082
              }}
            >
              ×
            </button>
            <p style={{ color: 'white', textAlign: 'center', marginBottom: 8, fontSize: 13 }}>
              {current.name}
              {images.length > 1 && (
                <span style={{ marginLeft: 10, opacity: 0.75 }}>
                  ({imageIndex + 1} / {images.length})
                </span>
              )}
            </p>
            <ZoomableImage
              url={current.url}
              alt={current.name}
              width={layout.imageWidth}
              height="calc(100vh - var(--header-height, 52px) - 44px)"
            />
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => setImageIndex((i) => Math.max(0, i - 1))}
                  disabled={imageIndex <= 0}
                  title="Previous image"
                  style={{
                    position: 'absolute', top: '50%', left: 10, transform: 'translateY(-50%)',
                    background: 'rgba(0,0,0,0.55)', color: 'white',
                    border: '1px solid rgba(255,255,255,0.45)', borderRadius: '50%',
                    width: 40, height: 40, cursor: imageIndex <= 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  <i className="fa-solid fa-chevron-left"></i>
                </button>
                <button
                  type="button"
                  onClick={() => setImageIndex((i) => Math.min(images.length - 1, i + 1))}
                  disabled={imageIndex >= images.length - 1}
                  title="Next image"
                  style={{
                    position: 'absolute', top: '50%', right: 10, transform: 'translateY(-50%)',
                    background: 'rgba(0,0,0,0.55)', color: 'white',
                    border: '1px solid rgba(255,255,255,0.45)', borderRadius: '50%',
                    width: 40, height: 40, cursor: imageIndex >= images.length - 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  <i className="fa-solid fa-chevron-right"></i>
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <div
        className="modal-overlay"
        onClick={onClose}
        style={{ justifyContent: 'flex-start', paddingRight: layout.pad, background: 'rgba(0,0,0,0.5)', zIndex: 10080 }}
      >
        <div
          className="modal-content"
          onClick={(event) => event.stopPropagation()}
          style={{
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            maxWidth: 'none', width: layout.width,
            margin: '20px 0 20px 16px', maxHeight: 'calc(100vh - 40px)'
          }}
        >
          <div className="modal-header" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12, flexShrink: 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: '1 1 220px' }}>
              <h3 className="modal-title" style={{ marginBottom: 2 }}>{viewing.student_name}</h3>
              <p className="modal-subtitle" style={{ margin: 0 }}>
                Image: {viewing.image_name || '—'}
                {imageNote ? ` · ${imageNote}` : ''}
              </p>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'stretch' }}>
              {[
                { label: 'Class', value: viewing.class || '-', minWidth: 96 },
                { label: 'Section', value: viewing.section || '-', minWidth: 96 },
                { label: 'Subject', value: viewing.subject || '-', minWidth: 140 }
              ].map(({ label, value, minWidth }) => (
                <div key={label} className="modal-info-card" style={{ padding: '8px 14px', minWidth, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span className="modal-info-label" style={{ margin: 0, fontSize: '0.75rem' }}>{label}</span>
                  <span className="modal-info-value" style={{ margin: 0, fontSize: '1rem' }}>{value}</span>
                </div>
              ))}
              <div
                className={`modal-marks-card ${viewing.status === 'error' ? 'modal-marks-error' : 'modal-marks-success'}`}
                style={{ padding: '8px 18px', minWidth: 110, margin: 0, display: 'flex', flexDirection: 'column', gap: 2 }}
              >
                <span className="modal-info-label" style={{ margin: 0, fontSize: '0.75rem' }}>Marks</span>
                <span
                  className={`modal-marks-value ${viewing.status === 'error' ? 'marks-error' : 'marks-success'}`}
                  style={{ margin: 0, fontSize: '1.25rem' }}
                >
                  {marksLabel}
                </span>
              </div>
            </div>
            {hasRubric && (
              <button
                type="button"
                onClick={() => setShowRubric((open) => !open)}
                title={rubricOpen ? 'Hide rubric' : 'Show rubric'}
                style={{
                  padding: '7px 14px',
                  border: rubricOpen ? 'none' : '1px solid var(--border-color, #ddd)',
                  borderRadius: 8,
                  background: rubricOpen ? 'linear-gradient(135deg, #3B82F6, #2563EB)' : 'var(--select-bg, white)',
                  color: rubricOpen ? 'white' : 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  whiteSpace: 'nowrap',
                  marginLeft: 'auto'
                }}
              >
                <i className="fa-solid fa-book-open"></i>
                Rubrics
              </button>
            )}
            <button type="button" onClick={onClose} className="modal-close-btn" style={{ marginLeft: hasRubric ? 0 : 'auto' }}>
              <i className="fa-solid fa-times"></i> Close
            </button>
          </div>

          <div className="modal-body" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {canSave ? (
              <ModalCommentsEditor
                viewingComment={viewing}
                allowedQuestionIds={allowedQuestionIds}
                rubricQuestionMarks={rubricQuestionMarks}
                paperTotal={paperTotal}
                renderHighlights={renderHighlights}
                renderMarkdownTable={paintTable}
                apiBaseUrl={apiBaseUrl}
                onSaved={(newValue, marks) => {
                  publish({
                    ...viewing,
                    comments: newValue,
                    comments_custom: 1,
                    ...(marks != null ? { marks } : {})
                  });
                }}
                onTableSaved={(newTableMd, regeneratedComments, marks, tableHistory) => {
                  publish({
                    ...viewing,
                    table_comments: newTableMd,
                    comments: regeneratedComments,
                    comments_custom: 0,
                    ...(marks != null ? { marks } : {}),
                    ...(tableHistory !== undefined ? { table_history: tableHistory } : {})
                  });
                }}
                onError={() => {}}
                linkedQuestion={linkedQuestion}
                onFocusQuestion={focusQuestion}
                onHighlightsSaved={(tableHistory) => {
                  publish({
                    ...viewing,
                    ...(tableHistory !== undefined ? { table_history: tableHistory } : {})
                  });
                }}
              />
            ) : (
              <div className="modal-comments-card">
                <p className="modal-info-label" style={{ marginTop: 0 }}>Verification Table</p>
                {tablePreview ? paintTable(tablePreview) : (
                  <p className="modal-comments-text">{viewing.comments || 'No deduction table is saved for this student.'}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          position: 'fixed',
          top: 'var(--header-height, 52px)',
          right: layout.ocrRight,
          bottom: 0,
          width: layout.ocrWidth,
          background: 'var(--card-bg, #ffffff)',
          borderLeft: '1px solid var(--border, #E5E7EB)',
          boxShadow: '-4px 0 16px rgba(0,0,0,0.18)',
          zIndex: 10082,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div style={{
          padding: '14px 16px',
          borderBottom: '1px solid var(--border, #E5E7EB)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          flexShrink: 0
        }}>
          <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)' }}>
            <i className="fa-solid fa-file-lines" style={{ marginRight: 6, color: '#6D28D9' }}></i>
            OCR — {viewing.student_name}
          </h3>
          <span
            title={ratioPct || ocrMeta.label}
            style={{ background: ocrMeta.bg, color: 'white', padding: '2px 8px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 700 }}
          >
            {ocrMeta.label}{ratioPct ? ` · ${ratioPct}` : ''}
          </span>
          <button
            type="button"
            onClick={saveOcr}
            disabled={ocrSaving || ocrDraft === (viewing.ocr_text || '')}
            title="Save this student's OCR transcript. Ctrl+Enter"
            style={{
              marginLeft: 'auto',
              padding: '4px 10px',
              background: ocrDraft === (viewing.ocr_text || '') ? '#e5e7eb' : '#7c3aed',
              color: ocrDraft === (viewing.ocr_text || '') ? '#6b7280' : 'white',
              border: 'none',
              borderRadius: 6,
              fontWeight: 700,
              fontSize: 12,
              cursor: ocrSaving || ocrDraft === (viewing.ocr_text || '') ? 'default' : 'pointer'
            }}
          >
            {ocrSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
        <MarkedTextarea
          textareaRef={ocrRef}
          mark={linkedQuestion ? linkedQuestion.ocr : null}
          scrollTick={linkedQuestion ? linkedQuestion.tick : 0}
          dir="auto"
          value={ocrDraft}
          placeholder="No OCR transcript saved for this student yet."
          onChange={(event) => { setOcrDraft(event.target.value); setOcrNote(''); }}
          onKeyDown={(event) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
              event.preventDefault();
              saveOcr();
            }
          }}
          spellCheck={false}
          style={{
            flex: 1, width: '100%', minHeight: 0, resize: 'none',
            border: 'none', outline: 'none', padding: '12px 16px',
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, monospace',
            fontSize: '0.82rem', lineHeight: 1.5,
            background: 'transparent', color: 'var(--text-primary)'
          }}
        />
        <div style={{ flexShrink: 0, padding: '4px 16px 8px', fontSize: 12, minHeight: 22, color: !ocrNote ? 'var(--text-secondary, #6b7280)' : ocrNote === 'Saved' ? '#059669' : '#b91c1c' }}>
          {ocrNote && ocrNote !== 'Saved' ? ocrNote : (ocrNote === 'Saved' ? 'Saved for this student' : 'Edits apply only to this student')}
        </div>
      </div>

      {rubricOpen && (
        <div
          style={{
            position: 'fixed',
            top: 'var(--header-height, 52px)',
            right: 0,
            bottom: 0,
            width: '24vw',
            background: 'var(--card-bg, #ffffff)',
            borderLeft: '1px solid var(--border, #E5E7EB)',
            boxShadow: '-4px 0 16px rgba(0,0,0,0.18)',
            zIndex: 10082,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <div style={{
            padding: '14px 16px',
            borderBottom: '1px solid var(--border, #E5E7EB)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexShrink: 0
          }}>
            <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: 'var(--text-primary)', flex: 1, minWidth: 0 }}>
              <i className="fa-solid fa-book-open" style={{ marginRight: 6, color: '#2563EB' }}></i>
              Rubric
            </h3>
            <button
              type="button"
              onClick={saveRubric}
              disabled={rubricSaving || rubricDraft === savedRubric}
              title="Save this folder's rubric. Ctrl+Enter"
              style={{
                padding: '4px 10px',
                background: rubricDraft === savedRubric ? '#e5e7eb' : '#059669',
                color: rubricDraft === savedRubric ? '#6b7280' : 'white',
                border: 'none',
                borderRadius: 6,
                fontWeight: 700,
                fontSize: 12,
                cursor: rubricSaving || rubricDraft === savedRubric ? 'default' : 'pointer',
                flexShrink: 0
              }}
            >
              {rubricSaving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setShowRubric(false)}
              title="Close rubric"
              aria-label="Close rubric"
              style={{
                background: '#EF4444', color: 'white', border: 'none',
                borderRadius: '50%', width: 30, height: 30,
                cursor: 'pointer', fontSize: 16, lineHeight: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>
          <MarkedTextarea
            textareaRef={rubricRef}
            mark={linkedQuestion ? linkedQuestion.rubric : null}
            scrollTick={linkedQuestion ? linkedQuestion.tick : 0}
            dir="auto"
            value={rubricDraft}
            placeholder="No rubric text saved for this folder yet."
            onChange={(event) => { setRubricDraft(event.target.value); setRubricNote(''); }}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                event.preventDefault();
                saveRubric();
              }
            }}
            spellCheck={false}
            style={{
              flex: 1, width: '100%', minHeight: 0, resize: 'none',
              border: 'none', outline: 'none', padding: '14px 18px',
              font: 'inherit', fontSize: '0.86rem', lineHeight: 1.55,
              background: 'transparent', color: 'var(--text-primary)'
            }}
          />
          <div style={{ flexShrink: 0, padding: '4px 18px 8px', fontSize: 12, minHeight: 22, color: !rubricNote ? 'var(--text-secondary, #6b7280)' : rubricNote === 'Saved' ? '#059669' : '#b91c1c' }}>
            {rubricNote && rubricNote !== 'Saved' ? rubricNote : (rubricNote === 'Saved' ? 'Saved for this folder' : 'Edits apply to every student in this folder')}
          </div>
        </div>
      )}
    </div>
  );
}
